/**
 * frontend/src/pages/valuation/_NormalizeYearTable.tsx
 *
 * VAL-U2C-SCREEN-1 — fiscal years side by side. Revenue, cost of sales and
 * operating expenses are the stored figures; EBITDA, adjusted EBITDA and SDE are
 * the server's normalized figures (never computed in the browser). A figure the
 * server could not support renders empty, never 0.
 * VAL-PATH-ADJUST P4b — the cost line uses the one COGS term (owner-approved 10-21 20:53).
 */
import type { FinancialsRow } from '@/api/valuationFinancials'
import type { NormalizedYear } from '@/api/valuationNormalization'
import { COGS_LABEL } from './_FinancialsYearTable'

export function money(value: unknown): string {
  if (typeof value !== 'number' || Number.isNaN(value)) return ''
  return value.toLocaleString('en-US', { maximumFractionDigits: 2 })
}

const STORED: Array<{ key: 'revenue' | 'cogs' | 'operating_expenses'; label: string }> = [
  { key: 'revenue', label: 'Revenue' },
  { key: 'cogs', label: COGS_LABEL },
  { key: 'operating_expenses', label: 'Operating expenses' },
]
const NORMALIZED: Array<{ key: 'ebitda' | 'adjusted_ebitda' | 'sde'; label: string }> = [
  { key: 'ebitda', label: 'EBITDA' },
  { key: 'adjusted_ebitda', label: 'Adjusted EBITDA' },
  { key: 'sde', label: 'SDE' },
]

export function NormalizeYearTable({ rows, normalized }: { rows: FinancialsRow[]; normalized: NormalizedYear[] }) {
  const years = rows.map(r => r.year).sort((a, b) => a - b)
  const byYear = new Map(rows.map(r => [r.year, r]))
  const norm = new Map(normalized.map(n => [Number(n.year), n]))
  return (
    <div className="overflow-x-auto rounded-lg border border-pal-line bg-pal-white">
      <table aria-label="Figures for each of the fiscal years" className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 text-right text-pal-ink-soft">
            <th scope="col" className="p-2 text-left" />
            {years.map(y => <th key={y} scope="col" className="p-2 font-medium">FY{y}</th>)}
          </tr>
        </thead>
        <tbody>
          {STORED.map(f => (
            <tr key={f.key} className="border-b border-gray-100">
              <th scope="row" className="p-2 text-left font-normal text-pal-ink-body">{f.label}</th>
              {years.map(y => <td key={y} className="p-2 text-right tabular-nums">{money(byYear.get(y)?.[f.key])}</td>)}
            </tr>
          ))}
          {NORMALIZED.map(f => (
            <tr key={f.key} className="border-b border-gray-100 font-medium last:border-0">
              <th scope="row" className="p-2 text-left">{f.label}</th>
              {years.map(y => <td key={y} className="p-2 text-right tabular-nums">{money(norm.get(y)?.[f.key])}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
